import React, { useEffect, useRef } from 'react'

const lightBlueBarColor = 'rgb(26, 134, 196, 0.3)'

export type Analyzable = Pick<AnalyserNode, 'frequencyBinCount' | 'getByteFrequencyData'>

export function FrequencyGraph({
  analyser,
  height,
  barColor = lightBlueBarColor,
}: {
  analyser: Analyzable
  height: number
  barColor?: string
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    let animationId: number

    const draw = () => {
      animationId = requestAnimationFrame(draw)
      analyser.getByteFrequencyData(dataArray)

      ctx.fillStyle = 'rgb(255, 255, 255)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Only show the lower portion of the frequency range (speech focused)
      const usableItems = Math.min(dataArray.length, 30)
      const barWidth = canvas.width / usableItems - 4
      const centerY = canvas.height / 2

      for (let i = 0; i < usableItems; i++) {
        const emphasis = Math.sin((i / usableItems) * Math.PI) * 1.2
        const barHeight = Math.max(minBarHeight(canvas.height), (dataArray[i] / 255) * canvas.height * emphasis)

        ctx.fillStyle = barColor
        const x = (i / usableItems) * canvas.width

        // Draw upper bar (rounded at top corners)
        roundedRect(ctx, x, centerY - barHeight, barWidth, barHeight, Math.min(barWidth / 2, barHeight / 2, 4), 'top')
        // Draw lower bar (rounded at bottom corners)
        roundedRect(ctx, x, centerY, barWidth, barHeight, Math.min(barWidth / 2, barHeight / 2, 4), 'bottom')
      }
    }

    draw()

    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [analyser])

  return <canvas ref={canvasRef} height={height} className="w-full ph-no-capture" />
}

function minBarHeight(canvasHeight: number) {
  return Math.max(Math.ceil(canvasHeight / 10), 5)
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  roundingPosition: 'top' | 'bottom' = 'top'
) {
  ctx.beginPath()
  if (roundingPosition === 'top') {
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
  } else {
    ctx.moveTo(x, y)
    ctx.lineTo(x + width, y)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y)
  }
  ctx.closePath()
  ctx.fill()
}
